/**
 * Sprint P21 · Feb 2026 · Cockpit Geological Modelling Workspace panel.
 *
 * Renders as a collapsible right-hand pane inside CockpitPage. Provides:
 *   · Grade Shell   — generate iso-surface from the block-model grade cube
 *   · Lithology     — generate convex-hull solids per lithology code
 *   · Topography    — Delaunay-triangulated surface from collar elevations
 *   · Assay Discs   — toggle on/off (backend already computes)
 *   · Object Tree   — list persisted geo_models with visibility toggle
 */
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Mountains, Cube as CubeIcon, Stack, DotsSix, Play, Trash,
  Eye, EyeSlash, DownloadSimple, Sparkle, TreeStructure, X, Minus,
  PushPin, PushPinSlash,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  getGeoModelSummary, getGeoLithologyCodes,
  generateGradeShell, generateLithologySolid, generateTopography,
  generateGradeShellBatch,
  deleteGeoModel, downloadGeoModelObj,
} from "@/lib/api";


export default function CockpitGeoWorkspace({ projectId, onModelSelect,
                                                    onModelVisibilityToggle,
                                                    visibility, onAssayDiscsToggle,
                                                    showAssayDiscs, onClose,
                                                    pinned = true, onPinToggle }) {
  const qc = useQueryClient();
  // ── Persist last-opened tab per user (P37 acceptance criterion) ─────
  const [tab, setTab] = useState(() => {
    try {
      const saved = window.localStorage.getItem("sres.cockpit.geoPanelTab");
      return (saved === "build" || saved === "tree") ? saved : "build";
    } catch { return "build"; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("sres.cockpit.geoPanelTab", tab); }
    catch { /* ignore */ }
  }, [tab]);
  const [collapsed, setCollapsed] = useState(false);
  const summaryQ = useQuery({
    queryKey: ["geo-summary", projectId],
    queryFn:  () => getGeoModelSummary(projectId),
    enabled:  !!projectId,
  });
  const lithosQ = useQuery({
    queryKey: ["geo-lithos", projectId],
    queryFn:  () => getGeoLithologyCodes(projectId),
    enabled:  !!projectId,
  });

  const [cutoff, setCutoff] = useState(0.5);
  const [batchCutoffs, setBatchCutoffs] = useState("0.1, 0.2, 0.5, 1.0");
  const [lithoCode, setLithoCode] = useState("");
  const [padding, setPadding] = useState(100);

  const genGradeShell = useMutation({
    mutationFn: () => generateGradeShell(projectId, { cutoff: Number(cutoff), smooth: 1 }),
    onSuccess:  (d) => {
      toast.success(`Grade shell built · ${d.n_vertices} verts · ${d.n_faces} faces`);
      qc.invalidateQueries({ queryKey: ["geo-summary", projectId] });
      onModelSelect?.(d.id);
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });

  // P21.2 · Multi-cutoff batch generation.
  const genBatch = useMutation({
    mutationFn: () => {
      const cutoffs = batchCutoffs.split(",").map((s) => parseFloat(s.trim()))
        .filter((n) => Number.isFinite(n));
      if (!cutoffs.length) throw new Error("Enter at least one cutoff");
      return generateGradeShellBatch(projectId, { cutoffs, smooth: 1 });
    },
    onSuccess: (d) => {
      const ok = d.succeeded?.length || 0;
      const err = d.failed?.length || 0;
      toast.success(`Batch built · ${ok} succeeded · ${err} failed`);
      qc.invalidateQueries({ queryKey: ["geo-summary", projectId] });
      // Auto-show all newly built shells.
      d.succeeded?.forEach((s) => onModelSelect?.(s.model_id));
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });

  const genLithology = useMutation({
    mutationFn: () => generateLithologySolid(projectId, { lithology_code: lithoCode }),
    onSuccess:  (d) => {
      toast.success(`Solid built for '${d.lithology}' · ${d.n_faces} faces`);
      qc.invalidateQueries({ queryKey: ["geo-summary", projectId] });
      onModelSelect?.(d.id);
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });

  const genTopo = useMutation({
    mutationFn: () => generateTopography(projectId, { padding_m: Number(padding) }),
    onSuccess:  (d) => {
      toast.success(`Topography · ${d.n_faces} faces`);
      qc.invalidateQueries({ queryKey: ["geo-summary", projectId] });
      onModelSelect?.(d.id);
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });

  const del = useMutation({
    mutationFn: (id) => deleteGeoModel(projectId, id),
    onSuccess:  () => {
      toast.success("Model deleted");
      qc.invalidateQueries({ queryKey: ["geo-summary", projectId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const models = summaryQ.data?.models || [];
  const lithos = lithosQ.data?.codes || [];

  return (
    <div className="w-full h-full flex flex-col bg-white border-l border-slate-200 animate-in slide-in-from-right-2 duration-200"
          data-testid="cockpit-geo-workspace">
      {/* Header — professional dockable-panel header */}
      <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
        <TreeStructure size={14} className="text-brand-blue" weight="fill" />
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-700 font-bold flex-1"
             data-testid="cockpit-geo-panel-title">
          Geo Model
        </div>
        <button type="button"
                onClick={() => setCollapsed((c) => !c)}
                title={collapsed ? "Expand" : "Collapse"}
                aria-label={collapsed ? "Expand panel" : "Collapse panel"}
                className="text-slate-500 hover:text-slate-900 rounded hover:bg-slate-200 w-6 h-6 flex items-center justify-center transition-colors"
                data-testid="cockpit-geo-collapse-btn">
          <Minus size={12} />
        </button>
        <button type="button"
                onClick={() => onPinToggle?.()}
                title={pinned ? "Pinned — click to Auto-Hide" : "Auto-Hide — click to Pin"}
                aria-label={pinned ? "Auto-Hide panel" : "Pin panel open"}
                aria-pressed={pinned}
                className={`rounded w-6 h-6 flex items-center justify-center transition-colors ${
                  pinned ? "text-brand-blue hover:bg-sky-50"
                          : "text-slate-400 hover:text-slate-700 hover:bg-slate-200"
                }`}
                data-testid="cockpit-geo-pin-btn">
          {pinned ? <PushPin size={12} weight="fill" /> : <PushPinSlash size={12} />}
        </button>
        <button type="button"
                onClick={() => onClose?.()}
                title="Close panel (Esc)"
                aria-label="Close panel"
                className="text-slate-500 hover:text-rose-600 rounded hover:bg-rose-50 w-6 h-6 flex items-center justify-center transition-colors"
                data-testid="cockpit-geo-close-btn">
          <X size={12} weight="bold" />
        </button>
      </div>

      {collapsed ? (
        <div className="px-3 py-2 text-[10px] text-slate-500 italic"
             data-testid="cockpit-geo-collapsed-hint">
          Panel collapsed. Click <b>–</b> above to expand.
        </div>
      ) : (
      <>
      <div className="flex border-b border-slate-200">
        <TabBtn active={tab === "build"} onClick={() => setTab("build")} testId="w-geo-tab-build" label="Build" />
        <TabBtn active={tab === "tree"}  onClick={() => setTab("tree")}  testId="w-geo-tab-tree"  label={`Objects (${models.length})`} />
      </div>

      <div className="flex-1 overflow-y-auto">
        {tab === "build" && (
          <div className="p-3 space-y-4 text-[12px]">
            {/* Grade shell */}
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Mountains size={12} className="text-amber-500" />
                <b className="text-slate-700">Grade shell (iso-surface)</b>
              </div>
              <div className="text-[10px] text-slate-500">Marching-cubes iso-surface on the block-model grade cube.</div>
              <div className="flex items-center gap-2">
                <Label className="text-[9px] font-mono uppercase text-slate-500">Cutoff</Label>
                <Input type="number" step="0.1" value={cutoff}
                        onChange={(e) => setCutoff(e.target.value)}
                        className="h-7 text-[11px] font-mono w-24"
                        data-testid="w-geo-shell-cutoff" />
                <span className="text-[10px] text-slate-400">g/t</span>
                <Button size="sm" className="h-7 text-[10px] ml-auto bg-brand-blue text-white"
                            onClick={() => genGradeShell.mutate()}
                            disabled={genGradeShell.isPending}
                            data-testid="w-geo-shell-build">
                  <Sparkle size={10} className="mr-1" />
                  {genGradeShell.isPending ? "Building…" : "Build"}
                </Button>
              </div>

              {/* P21.2 · Multi-cutoff batch */}
              <div className="flex items-center gap-2 pt-1">
                <Label className="text-[9px] font-mono uppercase text-slate-500">Batch</Label>
                <Input type="text" value={batchCutoffs}
                        onChange={(e) => setBatchCutoffs(e.target.value)}
                        className="h-7 text-[11px] font-mono flex-1"
                        placeholder="0.1, 0.2, 0.5, 1.0"
                        data-testid="w-geo-shell-batch-input" />
                <Button size="sm" variant="outline" className="h-7 text-[10px]"
                            onClick={() => genBatch.mutate()}
                            disabled={genBatch.isPending}
                            data-testid="w-geo-shell-batch-build">
                  <Sparkle size={10} className="mr-1" />
                  {genBatch.isPending ? "…" : "Batch"}
                </Button>
              </div>
            </div>

            {/* Lithology solid */}
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Stack size={12} className="text-emerald-500" />
                <b className="text-slate-700">Lithology solid</b>
              </div>
              <div className="text-[10px] text-slate-500">Convex-hull solid around all intervals of one lithology code.</div>
              <div className="flex items-center gap-2">
                <Select value={lithoCode} onValueChange={setLithoCode}>
                  <SelectTrigger className="h-7 text-[11px] font-mono w-40"
                                    data-testid="w-geo-litho-code">
                    <SelectValue placeholder="Choose code…" />
                  </SelectTrigger>
                  <SelectContent>
                    {lithos.map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.code} · {l.count}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="h-7 text-[10px] ml-auto bg-brand-blue text-white"
                            onClick={() => genLithology.mutate()}
                            disabled={!lithoCode || genLithology.isPending}
                            data-testid="w-geo-litho-build">
                  <Sparkle size={10} className="mr-1" />
                  {genLithology.isPending ? "Building…" : "Build"}
                </Button>
              </div>
            </div>

            {/* Topography */}
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Mountains size={12} className="text-yellow-500" />
                <b className="text-slate-700">Topography surface</b>
              </div>
              <div className="text-[10px] text-slate-500">Delaunay-triangulated mesh through collar elevations.</div>
              <div className="flex items-center gap-2">
                <Label className="text-[9px] font-mono uppercase text-slate-500">Pad</Label>
                <Input type="number" value={padding}
                        onChange={(e) => setPadding(e.target.value)}
                        className="h-7 text-[11px] font-mono w-20"
                        data-testid="w-geo-topo-pad" />
                <span className="text-[10px] text-slate-400">m</span>
                <Button size="sm" className="h-7 text-[10px] ml-auto bg-brand-blue text-white"
                            onClick={() => genTopo.mutate()}
                            disabled={genTopo.isPending}
                            data-testid="w-geo-topo-build">
                  <Sparkle size={10} className="mr-1" />
                  {genTopo.isPending ? "Building…" : "Build"}
                </Button>
              </div>
            </div>

            {/* Assay discs toggle */}
            <div className="space-y-1 border-t border-slate-200 pt-3">
              <div className="flex items-center gap-2">
                <DotsSix size={12} className="text-pink-500" />
                <b className="text-slate-700">Assay discs</b>
                <button type="button"
                        onClick={() => onAssayDiscsToggle?.(!showAssayDiscs)}
                        data-testid="w-geo-discs-toggle"
                        className={`ml-auto text-[10px] uppercase font-mono px-2 py-1 rounded ${
                          showAssayDiscs ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                        }`}>
                  {showAssayDiscs ? "ON" : "OFF"}
                </button>
              </div>
              <div className="text-[10px] text-slate-500">Coloured discs sized by grade at every persisted assay interval midpoint.</div>
            </div>
          </div>
        )}

        {tab === "tree" && (
          <div className="p-2 space-y-1 text-[11px] font-mono">
            {models.length === 0 && (
              <div className="text-center text-slate-400 py-6 text-[11px]">
                No geo-models yet. Build one under Build tab.
              </div>
            )}
            {models.map((m) => (
              <div key={m.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded border border-slate-200 bg-white"
                    data-testid={`w-geo-model-${m.id}`}>
                <div className="w-3 h-3 rounded border border-slate-300"
                      style={{ background: m.colour || "#94A3B8" }} />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-slate-800">
                    {m.kind === "grade_shell" && <CubeIcon size={9} className="inline mr-1" />}
                    {m.kind === "lithology_solid" && <Stack size={9} className="inline mr-1" />}
                    {m.kind === "topography" && <Mountains size={9} className="inline mr-1" />}
                    <b>{m.kind}</b>{m.lithology ? ` · ${m.lithology}` : ""}
                    {m.cutoff != null ? ` · ${m.cutoff}` : ""}
                  </div>
                  <div className="text-[9px] text-slate-500">
                    {m.n_faces} faces · {m.n_vertices} verts
                  </div>
                </div>
                <button type="button"
                        onClick={() => onModelVisibilityToggle?.(m.id)}
                        className="text-slate-500 hover:text-brand-blue"
                        data-testid={`w-geo-model-toggle-${m.id}`}>
                  {visibility?.[m.id] === false ? <EyeSlash size={12} /> : <Eye size={12} />}
                </button>
                <button type="button"
                        onClick={() => downloadGeoModelObj(projectId, m.id,
                          `${m.kind}_${m.lithology || m.cutoff || ""}`)}
                        className="text-slate-500 hover:text-brand-blue"
                        data-testid={`w-geo-model-obj-${m.id}`}>
                  <DownloadSimple size={12} />
                </button>
                <button type="button"
                        onClick={() => del.mutate(m.id)}
                        className="text-slate-400 hover:text-red-500"
                        data-testid={`w-geo-model-del-${m.id}`}>
                  <Trash size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}


function TabBtn({ active, onClick, label, testId }) {
  return (
    <button type="button" onClick={onClick}
            data-testid={testId}
            className={`flex-1 text-[10px] uppercase tracking-widest font-mono py-2 border-b-2 ${
              active
                ? "border-brand-blue text-brand-blue"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
      {label}
    </button>
  );
}
